/**
 * useVerificationData Hook
 *
 * Manages data loading and reference data for verification dialog.
 * Consolidates Redux selectors and data fetching logic.
 */

import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Hook to access all verification reference data from Redux
 *
 * @returns {Object} All verification reference data and state
 */
const useVerificationData = () => {
  // Master reference data
  const verifyMasterReferenceData = useSelector(
    (state) => state.verifyMasterReferenceData
  );

  // Individual treatment reference data (7 days)
  const verificationIndividual1 = useSelector(
    (state) => state.verificationIndividual1
  );
  const verificationIndividual2 = useSelector(
    (state) => state.verificationDynamicMasterDate
  );
  const verificationIndividual3 = useSelector(
    (state) => state.verificationIndividual3
  );
  const verificationIndividual4 = useSelector(
    (state) => state.verificationIndividual4
  );
  const verificationIndividual5 = useSelector(
    (state) => state.verificationIndividual5
  );
  const verificationIndividual6 = useSelector(
    (state) => state.verificationIndividual6
  );
  const verificationIndividual7 = useSelector(
    (state) => state.verificationIndividual7
  );

  // Group treatment reference data (7 days)
  const verificationGroup1 = useSelector(
    (state) => state.verificationGroup1
  );
  const verificationGroup2 = useSelector(
    (state) => state.verificationGroupMasterData
  );
  const verificationGroup3 = useSelector(
    (state) => state.verificationGroup3
  );
  const verificationGroup4 = useSelector(
    (state) => state.verificationGroup4
  );
  const verificationGroup5 = useSelector(
    (state) => state.verificationGroup5
  );
  const verificationGroup6 = useSelector(
    (state) => state.verificationGroup6
  );
  const verificationGroup7 = useSelector(
    (state) => state.verificationGroup7
  );

  // Discharge treatment reference data (7 days)
  const verificationDischarge1Base = useSelector(
    (state) => state.verificationDischarge1
  );
  const verificationDischarge2Base = useSelector(
    (state) => state.verificationDischargeMasterData
  );
  const verificationDischarge3Base = useSelector(
    (state) => state.verificationDischarge3
  );
  const verificationDischarge4Base = useSelector(
    (state) => state.verificationDischarge4
  );
  const verificationDischarge5Base = useSelector(
    (state) => state.verificationDischarge5
  );
  const verificationDischarge6Base = useSelector(
    (state) => state.verificationDischarge6
  );
  const verificationDischarge7Base = useSelector(
    (state) => state.verificationDischarge7
  );

  // Verification dialog state
  const verificationDialogPopup = useSelector(
    (state) => state.VerificationDialogPopupSlice?.VerificationDialogPopup
  );

  // Add "<Clear Entry>" option to discharge dropdowns
  const verificationDischarge1 = useMemo(() => {
    return !verificationDischarge1Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge1Base];
  }, [verificationDischarge1Base]);

  const verificationDischarge2 = useMemo(() => {
    return !verificationDischarge2Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge2Base];
  }, [verificationDischarge2Base]);

  const verificationDischarge3 = useMemo(() => {
    return !verificationDischarge3Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge3Base];
  }, [verificationDischarge3Base]);

  const verificationDischarge4 = useMemo(() => {
    return !verificationDischarge4Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge4Base];
  }, [verificationDischarge4Base]);

  const verificationDischarge5 = useMemo(() => {
    return !verificationDischarge5Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge5Base];
  }, [verificationDischarge5Base]);

  const verificationDischarge6 = useMemo(() => {
    return !verificationDischarge6Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge6Base];
  }, [verificationDischarge6Base]);

  const verificationDischarge7 = useMemo(() => {
    return !verificationDischarge7Base
      ? null
      : [{ id: null, value: '<Clear Entry>' }, ...verificationDischarge7Base];
  }, [verificationDischarge7Base]);

  // Group all individual reference data
  const individualReferenceData = useMemo(
    () => ({
      day1: verificationIndividual1,
      day2: verificationIndividual2,
      day3: verificationIndividual3,
      day4: verificationIndividual4,
      day5: verificationIndividual5,
      day6: verificationIndividual6,
      day7: verificationIndividual7,
    }),
    [
      verificationIndividual1,
      verificationIndividual2,
      verificationIndividual3,
      verificationIndividual4,
      verificationIndividual5,
      verificationIndividual6,
      verificationIndividual7,
    ]
  );

  // Group all group reference data
  const groupReferenceData = useMemo(
    () => ({
      day1: verificationGroup1,
      day2: verificationGroup2,
      day3: verificationGroup3,
      day4: verificationGroup4,
      day5: verificationGroup5,
      day6: verificationGroup6,
      day7: verificationGroup7,
    }),
    [
      verificationGroup1,
      verificationGroup2,
      verificationGroup3,
      verificationGroup4,
      verificationGroup5,
      verificationGroup6,
      verificationGroup7,
    ]
  );

  // Group all discharge reference data
  const dischargeReferenceData = useMemo(
    () => ({
      day1: verificationDischarge1,
      day2: verificationDischarge2,
      day3: verificationDischarge3,
      day4: verificationDischarge4,
      day5: verificationDischarge5,
      day6: verificationDischarge6,
      day7: verificationDischarge7,
    }),
    [
      verificationDischarge1,
      verificationDischarge2,
      verificationDischarge3,
      verificationDischarge4,
      verificationDischarge5,
      verificationDischarge6,
      verificationDischarge7,
    ]
  );

  // Return all data grouped by purpose
  return {
    // Master data
    verifyMasterReferenceData,

    // Individual treatment data
    individualReferenceData,
    verificationIndividual1,
    verificationIndividual2,
    verificationIndividual3,
    verificationIndividual4,
    verificationIndividual5,
    verificationIndividual6,
    verificationIndividual7,

    // Group treatment data
    groupReferenceData,
    verificationGroup1,
    verificationGroup2,
    verificationGroup3,
    verificationGroup4,
    verificationGroup5,
    verificationGroup6,
    verificationGroup7,

    // Discharge treatment data
    dischargeReferenceData,
    verificationDischarge1,
    verificationDischarge2,
    verificationDischarge3,
    verificationDischarge4,
    verificationDischarge5,
    verificationDischarge6,
    verificationDischarge7,

    // Dialog state
    verificationDialogPopup,
  };
};

export default useVerificationData;

/**
 * Hook to get reference data for a specific day
 *
 * @param {number} dayIndex - Day index (1-7)
 * @param {Object} allReferenceData - All reference data from useVerificationData
 * @returns {Object} Reference data for the specific day
 */
export const useDayReferenceData = (dayIndex, allReferenceData) => {
  return useMemo(() => {
    const dayKey = `day${dayIndex}`;

    return {
      individual: allReferenceData.individualReferenceData?.[dayKey],
      group: allReferenceData.groupReferenceData?.[dayKey],
      discharge: allReferenceData.dischargeReferenceData?.[dayKey],
    };
  }, [dayIndex, allReferenceData]);
};
